import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '@/config/api';

const initialStatus = { enabled: false, active: false, voiceDecision: 'pending' };

export default function useGameRecording({
  gameId, canControl, token, participantToken, getConnectionId,
}) {
  const [status, setStatus] = useState(initialStatus);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [consentModalRequested, setConsentModalRequested] = useState(false);
  const latestRequest = useRef(0);

  const url = gameId ? apiUrl('/api/games/' + gameId + '/recording') : '';
  const refresh = useCallback(async (signal) => {
    if (!url || (!canControl && !participantToken) || (canControl && !token)) {
      setStatus(initialStatus);
      return null;
    }
    const requestNumber = ++latestRequest.current;
    const headers = {};
    const qs = new URLSearchParams();
    if (canControl && token) headers.Authorization = 'Bearer ' + token;
    else qs.set('participantToken', participantToken);
    const result = await fetch(url + '/status' + (qs.size ? '?' + qs.toString() : ''), {
      headers, signal, cache: 'no-store',
    });
    if (!result.ok) {
      if (result.status === 401 || result.status === 403) {
        setStatus(initialStatus);
        return null;
      }
      throw new Error('RECORDING_STATUS_FAILED');
    }
    const data = await result.json();
    if (!signal?.aborted && requestNumber === latestRequest.current) {
      setStatus(data);
    }
    return data;
  }, [url, canControl, token, participantToken]);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal).catch(() => {});
    const interval = window.setInterval(() => {
      refresh(controller.signal).catch(() => {});
    }, 6000);
    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, [refresh]);

  const send = useCallback(async (action, payload = {}) => {
    if (!url) return false;
    setBusy(true);
    setError('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      const body = { ...payload };
      if (canControl && token) headers.Authorization = 'Bearer ' + token;
      else if (participantToken) {
        body.participantToken = participantToken;
        body.connectionId = getConnectionId?.() || '';
      } else throw new Error('AUTH_REQUIRED');
      const response = await fetch(url + '/' + action, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (!response.ok) {
        let payloadError;
        try { payloadError = await response.json(); } catch { payloadError = null; }
        throw new Error(payloadError?.code || 'RECORDING_REQUEST_FAILED');
      }
      // Prefer server-confirmed values over optimistically enabling voice or REC.
      await refresh();
      setConsentModalRequested(false);
      return true;
    } catch (e) {
      setError(e.message || 'RECORDING_REQUEST_FAILED');
      return false;
    } finally {
      setBusy(false);
    }
  }, [url, canControl, token, participantToken, getConnectionId, refresh]);

  const start = useCallback(() => send('start'), [send]);
  const stop = useCallback(() => send('stop'), [send]);
  const decideVoice = useCallback((allowed) => send('voice-consent', { allowed }), [send]);
  const requestVoice = useCallback((actorIdentity) => send('request-voice-consent', { actorIdentity }), [send]);

  return {
    status, busy, error, refresh, start, stop, decideVoice, requestVoice,
    consentModalRequested, setConsentModalRequested, setError,
  };
}
